import { useCallback, useMemo } from "react";

import type { QuestionnairePayload } from "../../form-builder/types";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import type { CreateQuestionnaireTaskParams } from "../services/questionnaireTaskWorkflowService";
import { questionnaireTaskWorkflowService } from "../services/questionnaireTaskWorkflowService";
import { oids } from "@/configs/Settings";

export interface SendQuestionnaireTasksArgs {
  organizationId: string;
  practitionerRoleReference: string;
  patientReference: string;
  /**
   * Quando o envio ocorre no contexto de um agendamento, repasse `Appointment/{id}` ou
   * apenas o id lógico (o serviço normaliza e ignora placeholders de UI).
   */
  appointmentReference?: string;
  /** Questionários selecionados (devem ter `id`). */
  questionnaires: QuestionnairePayload[];
}

function normalizeErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return "Erro inesperado ao enviar questionários.";
}

/**
 * Orquestra o envio de N questionários como N tasks no workflow FHIR.
 */
export function useQuestionnaireTaskSend() {
  const initialData = useMemo<fhir5.Task[]>(() => [], []);

  const runSendTask = useCallback(async (args: SendQuestionnaireTasksArgs) => {
    const {
      organizationId,
      practitionerRoleReference,
      patientReference,
      appointmentReference,
      questionnaires,
    } = args;

    if (!questionnaires.length) {
      throw new Error("Selecione ao menos um questionário.");
    }

    const paramsList: CreateQuestionnaireTaskParams[] = [];

    for (const questionnaire of questionnaires) {
      const questionnaireId = questionnaire.id?.trim();
      if (!questionnaireId) continue;
      paramsList.push({
        organizationId: `${oids.cnpj}-${organizationId}`,
        practitionerRoleReference,
        patientReference,
        questionnaireId,
        questionnaireTitle: questionnaire.title,
        ...(String(appointmentReference ?? "").trim()
          ? { appointmentReference: String(appointmentReference).trim() }
          : {}),
      });
    }

    if (!paramsList.length) {
      throw new Error(
        "Nenhum questionário válido (ID ausente). Salve ou publique o questionário antes de enviar.",
      );
    }

    return questionnaireTaskWorkflowService.createQuestionnaireTasks(
      paramsList,
    );
  }, []);

  const { run, loading, error, reset, data } = useAsyncTask<
    [SendQuestionnaireTasksArgs],
    fhir5.Task[],
    string
  >({
    task: runSendTask,
    initialData,
    mapError: normalizeErrorMessage,
  });

  return {
    sendQuestionnaireTasks: run,
    isSending: loading,
    sendError: error,
    sentTasks: data ?? initialData,
    resetSendState: reset,
  };
}
